import ApiService from "@/core/services/api.service";

const INSTRUCTOR_ENDPOINT = "/api/instructor";
const USER_ENDPOINT = "/api/user";
const TITLE_ENDPOINT = "/api/title";
const FALLBACK_PHOTO = "https://cdn.anzera.com.tr/softinya/not-found.png";

export const INSTRUCTOR_TYPES = Object.freeze({
  INTERNAL: "internal",
  EXTERNAL: "external",
});

export const INSTRUCTOR_STATUSES = Object.freeze({
  ACTIVE: 1,
  PASSIVE: 0,
});

const unwrapEntity = (response) => {
  const payload = response?.data;
  if (
    payload &&
    payload.data &&
    !Array.isArray(payload.data) &&
    typeof payload.data === "object"
  ) {
    return payload.data;
  }
  return payload || {};
};

const unwrapCollection = (response) => {
  const payload = response?.data || {};

  if (Array.isArray(payload)) {
    return {
      data: payload,
      current_page: 1,
      last_page: 1,
      per_page: payload.length,
      total: payload.length,
    };
  }

  if (Array.isArray(payload.data)) {
    return {
      ...payload,
      ...(payload.meta || {}),
      data: payload.data,
    };
  }
  if (payload.data && Array.isArray(payload.data.data)) {
    return {
      ...payload.data,
      ...(payload.data.meta || payload.meta || {}),
      data: payload.data.data,
    };
  }

  return {
    data: [],
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0,
  };
};

const getRelationName = (relation) => {
  if (!relation) return "";
  if (typeof relation === "string") return relation;
  return relation.name || relation.title || "";
};

export const normalizeUser = (user = {}) => {
  const fullName =
    [user.name, user.surname].filter(Boolean).join(" ").trim() ||
    user.full_name ||
    "";

  return {
    id: Number(user.id),
    name: fullName,
    email: user.email || user.mail || "",
    photo: user.photo_link || user.photo || FALLBACK_PHOTO,
    title: getRelationName(user.title) || user.title_name || "",
    position: getRelationName(user.position) || user.position_name || "",
    department:
      getRelationName(user.department) || user.department_name || "",
    statusId: Number(user.status_id),
    raw: user,
  };
};

export const normalizeInstructor = (instructor = {}) => {
  const user = normalizeUser(instructor.user || {});

  return {
    id: Number(instructor.id),
    userId: Number(instructor.user_id || user.id),
    user,
    name: user.name || instructor.name || "",
    photo: user.photo || FALLBACK_PHOTO,
    title:
      user.title
      || user.position
      || instructor.title
      || instructor.title_name
      || instructor.position_name
      || "",
    department: user.department || instructor.department || "",
    type: instructor.type || INSTRUCTOR_TYPES.INTERNAL,
    institutionName: instructor.institution_name || "",
    expertiseAreas: Array.isArray(instructor.expertise_areas)
      ? instructor.expertise_areas
      : [],
    bio: instructor.bio || "",
    experienceYears: Number(instructor.experience_years) || null,
    educationsCertificates: Array.isArray(instructor.educations_certificates)
      ? instructor.educations_certificates
      : [],
    linkedinUrl: instructor.linkedin_url || "",
    statusId: Number(instructor.status_id),
    createdAt: instructor.created_at || "",
    raw: instructor,
  };
};

const getInstructorUserId = (instructor = {}) =>
  Number(instructor.user_id || instructor.user?.id);

const mergeInstructorUser = (instructor = {}, user = null) => ({
  ...instructor,
  user: user
    ? {
        ...(instructor.user || {}),
        ...user,
      }
    : instructor.user,
});

const fetchUserDetail = async (userId) => {
  if (!Number.isFinite(userId)) return null;
  const response = await ApiService.get(`${USER_ENDPOINT}/detail/${userId}`);
  return unwrapEntity(response);
};

const instructorService = {
  async list(params) {
    const response = await ApiService.query(INSTRUCTOR_ENDPOINT, params);
    const pagination = unwrapCollection(response);
    const items = pagination.data.map(normalizeInstructor);

    return {
      items,
      pagination: {
        ...pagination,
        data: items,
      },
    };
  },

  async get(id) {
    const response = await ApiService.get(INSTRUCTOR_ENDPOINT, id);
    const instructor = unwrapEntity(response);
    const currentUser = await fetchUserDetail(
      getInstructorUserId(instructor)
    ).catch(() => null);

    return normalizeInstructor(mergeInstructorUser(instructor, currentUser));
  },

  async create(payload) {
    const response = await ApiService.post(INSTRUCTOR_ENDPOINT, payload);
    return normalizeInstructor(unwrapEntity(response));
  },

  async update(id, payload) {
    const response = await ApiService.update(INSTRUCTOR_ENDPOINT, id, payload);
    return normalizeInstructor(unwrapEntity(response));
  },

  async updateStatus(id, statusId) {
    return this.update(id, { status_id: statusId });
  },

  async bulkUpdateStatus(ids, statusId) {
    return ApiService.put(`${INSTRUCTOR_ENDPOINT}/bulk-status`, {
      instructors: ids,
      status_id: Number(statusId),
    });
  },

  async remove(id) {
    return ApiService.delete(`${INSTRUCTOR_ENDPOINT}/${id}`);
  },

  async bulkRemove(ids) {
    return ApiService.delete(`${INSTRUCTOR_ENDPOINT}/bulk-destroy`, {
      data: {
        instructors: ids,
      },
    });
  },

  async counts() {
    const countParams = {
      page: 1,
      per_page: 1,
      sort: "id",
      order: "desc",
    };
    const [allResponse, activeResponse, passiveResponse] = await Promise.all([
      ApiService.query(INSTRUCTOR_ENDPOINT, countParams),
      ApiService.query(INSTRUCTOR_ENDPOINT, {
        ...countParams,
        status_id: INSTRUCTOR_STATUSES.ACTIVE,
      }),
      ApiService.query(INSTRUCTOR_ENDPOINT, {
        ...countParams,
        status_id: INSTRUCTOR_STATUSES.PASSIVE,
      }),
    ]);

    const total = unwrapCollection(allResponse);
    const active = unwrapCollection(activeResponse);
    const passive = unwrapCollection(passiveResponse);

    return {
      active: Number(active.total) || 0,
      passive: Number(passive.total) || 0,
      total: Number(total.total) || 0,
    };
  },

  export(params = {}) {
    return ApiService.post(`${INSTRUCTOR_ENDPOINT}/export`, params, {
      responseType: "blob",
    });
  },

  async listTitles() {
    const response = await ApiService.query(TITLE_ENDPOINT, {});
    return unwrapCollection(response).data
      .map((title) => ({
        id: Number(title.id),
        name: title.name || "",
      }))
      .filter((title) => Number.isFinite(title.id) && title.name);
  },

  async listUsers(params = {}) {
    const response = await ApiService.query(USER_ENDPOINT, {
      page: 1,
      per_page: 1000,
      order: "desc",
      sort: "created_at",
      status_id: 1,
      ...params,
    });
    return unwrapCollection(response)
      .data.map(normalizeUser)
      .filter((user) => !user.raw.instructor);
  },

  async listActiveInstructorOptions(params = {}) {
    const response = await ApiService.query(USER_ENDPOINT, {
      is_instructor: 1,
      like: "",
      page: 1,
      per_page: 1000,
      order: "asc",
      sort: "name",
      status_id: 1,
      ...params,
    });

    return unwrapCollection(response)
      .data.map((userData) => {
        const user = normalizeUser(userData);
        const instructor = normalizeInstructor({
          ...(userData.instructor || {}),
          user: userData,
        });

        return {
          ...instructor,
          user,
          name: user.name,
        };
      })
      .filter((instructor) => Number.isFinite(instructor.id));
  },
};

export default instructorService;
